/*
Trace
- Satisfies: FRN-003 (Dashboard KPIs/Liste), FRN-004 (Bookings Liste/Details)
- Uses Interfaces: IF-/bookings (GET, POST), IF-/bookings/{bookingReference} (GET), IF-/webhooks/events (GET)
- Data Models: Booking (canonical)
*/
import { notifyBookingsChanged } from './bookingEvents';
import { reasonFields, MAX_NOTE } from '../utils/discardReason';
import { signOut } from 'firebase/auth';

import { auth } from '../config/firebase';
import { BookingApiResponse, Booking, BookingUpdatePayload } from '../types/booking';
import { transformApiResponse } from '../utils/bookingAdapter';
import { http } from './http';

// Create axios instance with automatic Firebase authentication
const getApiInstance = () => {
  // Use the robust authenticated API client with interceptors
  // Bookings endpoints can be slow (large datasets / complex filters) → allow longer timeout
  return http;
};

// The gateway returns offset/limit/total; older responses return page/totalPages.
// Normalize once here so every booking reader follows the same pagination contract.
function bookingPagination(pagination: any) {
  const limit = pagination.limit || 50;
  const total = pagination.totalItems ?? pagination.total;
  return {
    ...pagination, total,
    currentPage: pagination.currentPage ?? pagination.page ?? Math.floor((pagination.offset ?? 0) / limit) + 1,
    totalPages: pagination.totalPages ?? Math.max(1, Math.ceil((total ?? 0) / limit)),
  };
}

export const bookingService = {
  async createInIloca(id: string, confirmedNotCreated = false, existingIlocaBookingId?: string, confirmedLink = false, rejectedCandidateIds: string[] = []): Promise<{ ilocaBookingId: string; reference?: string; confirmationRequired?: boolean; preview?: { checkIn: string | null; checkOut: string | null; categoryId: number | null; categoryName?: string | null; guestName: string } }> {
    const response = await getApiInstance().post(`/api/bookings/${encodeURIComponent(id)}/create-in-iloca`, { confirmedNotCreated, ...(rejectedCandidateIds.length ? { rejectedCandidateIds } : {}), ...(existingIlocaBookingId ? { existingIlocaBookingId, confirmedLink } : {}) });
    if (!response.data?.success) throw new Error(response.data?.message || 'iLoca-Anlage fehlgeschlagen.');
    notifyBookingsChanged({ bookingId: id });
    return response.data.data;
  },
  async unlinkIloca(id: string): Promise<void> {
    const response = await getApiInstance().post(`/api/bookings/${encodeURIComponent(id)}/unlink-iloca`);
    if (!response.data?.success) throw new Error(response.data?.message || 'Zuordnung konnte nicht gelöst werden.');
    notifyBookingsChanged({ bookingId: id });
  },
  async markEnteredInIloca(id: string): Promise<void> {
    const response = await getApiInstance().post(`/api/bookings/${encodeURIComponent(id)}/entered-in-iloca`);
    if (!response.data?.success) throw new Error('Buchung konnte nicht abgeschlossen werden.');
    notifyBookingsChanged({ enteredInIlocaId: id });
  },

  // Get bookings - aligned with OpenAPI spec (GET /bookings)
  async getBookings(startDate?: Date, endDate?: Date, filters?: {
    includeArchived?: boolean;
    status?: string;
    source?: string;
    room?: string;
    search?: string;
    dateFrom?: string; // NEW: Date range from (as string YYYY-MM-DD)
    dateTo?: string; // NEW: Date range to (as string YYYY-MM-DD)
    stayFrom?: string;
    stayTo?: string;
    limit?: number;
    offset?: number;
    page?: number; // NEW: Page parameter for pagination
    needsApproval?: boolean; // DRE-755: Filter for pending approvals
    rechnungAngefordert?: string; // Filter: Rechnung angefordert
  }): Promise<Booking[] | { data: Booking[], pagination: any }> {
    const params: any = {};
    try {
      const api = getApiInstance();
      // Use dateFrom/dateTo - prioritize filters.dateFrom/dateTo over startDate/endDate
      if (filters?.dateFrom) {
        params.dateFrom = filters.dateFrom;
      } else if (startDate) {
        params.dateFrom = startDate.toISOString().split('T')[0];
      }
      if (filters?.dateTo) {
        params.dateTo = filters.dateTo;
      } else if (endDate) {
        params.dateTo = endDate.toISOString().split('T')[0];
      }
      if (filters) {
        if (filters.stayFrom) params.stayFrom = filters.stayFrom;
        if (filters.stayTo) params.stayTo = filters.stayTo;
        if (filters.includeArchived) params.includeArchived = 'true';
        // Status filter matches OpenAPI enum values (CONFIRMED, CANCELLED, etc.)
        if (filters.status && filters.status !== 'all') {
          // Map frontend status values to backend enum values
          const statusMap: Record<string, string> = {
            'confirmed': 'CONFIRMED',
            'tentative': 'PENDING',
            'cancelled': 'CANCELLED',
            'checked_in': 'CONFIRMED', // Map to CONFIRMED for now
            'checked_out': 'CONFIRMED' // Map to CONFIRMED for now
          };
          params.status = statusMap[filters.status.toLowerCase()] || filters.status.toUpperCase();
        }
        // Source filter
        if (filters.source && filters.source !== 'all') {
          params.source = filters.source;
        }
        if (filters.room && filters.room.trim()) params.room = filters.room.trim();
        if (filters.search && filters.search.trim()) params.search = filters.search.trim();
        if (typeof filters.limit === 'number') params.limit = filters.limit;
        if (typeof filters.offset === 'number') params.offset = filters.offset;
        if (typeof filters.page === 'number') params.page = filters.page; // NEW: Page parameter
        if (filters.needsApproval) params.needsApproval = 'true'; // DRE-755: Pending approvals filter
        if (filters.rechnungAngefordert && filters.rechnungAngefordert !== 'all') {
          params.rechnungAngefordert = filters.rechnungAngefordert;
        }
      }
      // Use api-gateway endpoint with relative path
      const endpoint = '/api/bookings';
      const response = await api.get(endpoint, { params });
      
      if (!response.data?.success) {
        throw new Error(response.data?.error?.message || response.data?.error || 'Buchungen konnten nicht geladen werden.');
      }
      
      // Check if response includes pagination metadata
      if (response.data?.pagination) {
        // Return paginated response with metadata
        const transformed = transformApiResponse(response.data);
        return {
          data: transformed.data || [],
          pagination: bookingPagination(response.data.pagination)
        };
      } else {
        // Legacy response format - just return array
        const transformed = transformApiResponse(response.data);
        return transformed.data || [];
      }
    } catch (error: any) {
      console.error('API call failed:', error);
      
      if (error?.code === 'ECONNABORTED') {
        throw new Error('Die Anfrage hat zu lange gedauert (Timeout). Bitte erneut versuchen.');
      }

      // Handle 401 - redirect to login if not authenticated
      if (error.response?.status === 401 || error.status === 401) {
        // Check if user is actually logged in via Firebase Auth
        if (!auth.currentUser) {
          // User is not logged in, redirect to login
          window.location.href = '/login';
          throw new Error('Bitte anmelden, um die Buchungen zu laden.');
        } else {
          // User is logged in but token might be expired - try to refresh
          try {
            const _idToken = await auth.currentUser.getIdToken(true); // Force refresh
            // Retry with new token
            const api = getApiInstance();
            const response = await api.get('/api/bookings', { params });
            if (response.data?.success) {
              const transformed = transformApiResponse(response.data);
              return response.data?.pagination ? {
                data: transformed.data || [],
                pagination: bookingPagination(response.data.pagination)
              } : transformed.data || [];
            }
          } catch (refreshError) {
            // Token refresh failed - logout and redirect
            console.error('Token refresh failed:', refreshError);
            await signOut(auth);
            window.location.href = '/login';
            throw new Error('Die Sitzung ist abgelaufen. Bitte erneut anmelden.');
          }
        }
      }
      
      // Preserve HTTP status and server payload for the shared UI error translation.
      throw error;
    }
  },

  /** Inclusive stay overlap, including long stays; exhaust the gateway's pagination. */
  async getStayBookings(stayFrom: string, stayTo: string, filters: { includeArchived?: boolean } = {}): Promise<Booking[]> {
    const bookings = new Map<string, Booking>();
    for (let page = 1; ; page++) {
      const response = await bookingService.getBookings(undefined, undefined, { ...filters, stayFrom, stayTo, limit: 500, page });
      const data = Array.isArray(response) ? response : response.data;
      for (const booking of data) bookings.set(booking.id ?? `${booking.source}:${booking.bookingReference}`, booking);
      if (page > 1 && !data.length) throw new Error('Buchungsabfrage unvollständig: Eine Folgeseite ist leer. Bitte erneut laden.');
      if (Array.isArray(response) || page >= response.pagination.totalPages) break;
    }
    return [...bookings.values()];
  },

  // Get a single booking - aligned with OpenAPI spec (GET /bookings/{bookingReference})
  async getBooking(_source: string, reference: string): Promise<Booking> {
    try {
      const api = getApiInstance();
      // Use api-gateway endpoint with relative path
      const endpoint = `/api/bookings/${encodeURIComponent(reference)}`;
      const response = await api.get(endpoint);
      
      if (!response.data?.success || !response.data?.data) {
        throw new Error(response.data?.error?.message || response.data?.error || 'Booking not found');
      }
      
      return response.data.data as Booking;
    } catch (error: any) {
      console.error('API call failed:', error);

      if (error?.code === 'ECONNABORTED') {
        throw new Error('Die Anfrage hat zu lange gedauert (Timeout). Bitte erneut versuchen.');
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Verbindung zum Buchungsservice fehlgeschlagen.'
      );
    }
  },

  // Update an existing booking
  async updateBooking(bookingId: string, updates: BookingUpdatePayload): Promise<Booking> {
    if (!bookingId) {
      throw new Error('Booking ID is required');
    }

    const api = getApiInstance();
    // Use api-gateway endpoint
    // Backend handles resolution by ID
    const endpoint = `/api/bookings/${bookingId}`;
    
    const response = await api.put<BookingApiResponse>(endpoint, updates);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update booking');
    }

    if (!response.data.data) {
      throw new Error('Invalid response: missing booking data');
    }

    // Note: Booking update is already processed by PUT endpoint, no additional processing needed

    notifyBookingsChanged({ bookingId });
    return response.data.data as Booking;
  },

  // DRE-673: Approve or reject booking escalation
  async approveEscalation(bookingId: string, action: 'approve' | 'reject', userId?: string, reason?: string): Promise<Booking> {
    if (!bookingId) {
      throw new Error('Booking ID is required');
    }

    const api = getApiInstance();
    const endpoint = `/api/bookings/${bookingId}/approve`;

    const response = await api.post<BookingApiResponse>(endpoint, { action, userId, ...reasonFields(reason, 'reason', MAX_NOTE) });

    if (!response.data.success) {
      throw new Error(response.data.error || `Failed to ${action} escalation`);
    }

    if (!response.data.data?.booking) {
      throw new Error('Invalid response: missing booking data');
    }

    notifyBookingsChanged({ bookingId });
    return response.data.data.booking as Booking;
  },

  // Get booking with special requests
  async getBookingWithDetails(bookingId: string): Promise<Booking> {
    const api = getApiInstance();
    // Use standard booking endpoint (no /details suffix - that endpoint doesn't exist)
    const endpoint = `/api/bookings/${encodeURIComponent(bookingId)}`;
    
    const response = await api.get<BookingApiResponse>(endpoint);
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch booking details');
    }

    return transformApiResponse(response.data).data as Booking;
  },

};
